import { type Profile, profileRank } from './profiles';
import { type Role, requiredRoles } from './roles';

export const CHECKPOINT_LABEL_PREFIX = 'asdp-checkpoint:';

export const CHECKPOINTS = [
  'gate1',
  'requirements',
  'setup-output',
  'apply-config',
  'profile-lower',
  'roles',
] as const;
export type Checkpoint = (typeof CHECKPOINTS)[number];

export const isCheckpoint = (value: unknown): value is Checkpoint =>
  typeof value === 'string' && (CHECKPOINTS as readonly string[]).includes(value);

export const CHECKPOINT_INFO: Readonly<
  Record<Checkpoint, { title: string; gate: string; branchPrefix: string }>
> = {
  gate1: { title: 'Classify and plan', gate: 'Gate 1', branchPrefix: 'portal/gate1-' },
  requirements: { title: 'Requirements', gate: 'Gate 2', branchPrefix: 'portal/requirements-' },
  'setup-output': { title: 'Setup review', gate: 'Gates 2 + 3', branchPrefix: 'setup/' },
  'apply-config': { title: 'Apply setup', gate: 'Gate 4', branchPrefix: 'setup/apply-config-' },
  'profile-lower': { title: 'Lower the profile', gate: 'PRF-02', branchPrefix: 'portal/profile-' },
  roles: { title: 'People and roles', gate: 'ROL-02', branchPrefix: 'portal/roles-' },
};

export const checkpointLabel = (checkpoint: Checkpoint) =>
  `${CHECKPOINT_LABEL_PREFIX}${checkpoint}`;

/**
 * The requirement agent's documents PR. The agent opens it without a
 * checkpoint label, so it is known by its own label or branch.
 */
export const REQUIREMENT_AGENT_LABEL = 'agent:requirement-agent';
export const REQUIREMENT_DOCUMENTS_BRANCH_PREFIX = 'requirements-docs/';

/** The checkpoint a PR is: its checkpoint label, else `setup-output` for the requirement agent's documents. */
export function checkpointOfPull(pull: {
  labels?: ReadonlyArray<string | { name?: string }>;
  head: { ref: string };
}): Checkpoint | undefined {
  const labels = pull.labels ?? [];
  const labelled = checkpointOfLabels(labels);
  if (labelled) return labelled;
  const fromAgent =
    labels.some(label => (typeof label === 'string' ? label : label.name) === REQUIREMENT_AGENT_LABEL) ||
    pull.head.ref.startsWith(REQUIREMENT_DOCUMENTS_BRANCH_PREFIX);
  return fromAgent ? 'setup-output' : undefined;
}

/** The checkpoint a PR is, from its labels; undefined for an ordinary PR. */
export function checkpointOfLabels(
  labels: ReadonlyArray<string | { name?: string }>,
): Checkpoint | undefined {
  for (const label of labels) {
    const name = typeof label === 'string' ? label : label.name ?? '';
    if (name.startsWith(CHECKPOINT_LABEL_PREFIX)) {
      const id = name.slice(CHECKPOINT_LABEL_PREFIX.length);
      if (isCheckpoint(id)) return id;
    }
  }
  return undefined;
}

export type Approval = {
  role: Role;
  /** A decision owner decides; the others concur. */
  kind: 'decides' | 'concurs';
};

export type RoutingContext = {
  personalData?: boolean;
  /** Gate 1's contract names a Customer Acceptance Authority. */
  customerAcceptance?: boolean;
};

type RoutingRule = (profile: Profile, context: RoutingContext) => Approval[];

const decides = (role: Role): Approval => ({ role, kind: 'decides' });
const concurs = (role: Role): Approval => ({ role, kind: 'concurs' });
const atLeast = (profile: Profile, floor: Profile) =>
  profileRank(profile) >= profileRank(floor);

const gate2: RoutingRule = () => [
  decides('product_owner'),
  concurs('business_analyst'),
  concurs('tester_qa'),
  concurs('security_champion'),
];

const gate3: RoutingRule = (_profile, { personalData }) => [
  decides('architect'),
  concurs('security_champion'),
  ...(personalData ? [concurs('dpo')] : []),
  concurs('devsecops'),
];

/**
 * The portal's default routing, ASDP §3.2's gate table, for templates that
 * don't declare their own. A template's `sdlc.json` overrides it; see
 * routingFromSdlc. Either way fitToTeam applies.
 */
export const DEFAULT_ROUTING: Readonly<Record<Checkpoint, RoutingRule>> = {
  gate1: (_profile, { personalData }) => [
    decides('product_owner'),
    concurs('project_manager'),
    concurs('security_champion'),
    ...(personalData ? [concurs('dpo')] : []),
  ],
  requirements: gate2,
  'setup-output': (profile, context) => [
    ...gate2(profile, context),
    ...gate3(profile, context),
  ],
  'apply-config': () => [
    decides('project_manager'),
    concurs('architect'),
    concurs('tester_qa'),
    concurs('security_champion'),
    concurs('devsecops'),
  ],
  'profile-lower': () => [decides('product_owner'), decides('security_champion')],
  roles: () => [decides('product_owner'), decides('security_champion')],
};

/** Who proposes the checkpoint, which sending it covers (§3.2). */
const PROPOSER: Partial<Record<Checkpoint, Role>> = {
  gate1: 'project_manager',
  requirements: 'business_analyst',
};

/**
 * Fits a gate's approvals to the team the profile requires: the proposer and
 * roles the profile doesn't require drop out, each role counts once (deciding
 * wins), and from P2 the Security Governance Office decides Gate 3 through
 * the SAR, so the Architect concurs.
 */
export function fitToTeam(
  approvals: readonly Approval[],
  checkpoint: Checkpoint,
  profile: Profile,
  context: RoutingContext = {},
): Approval[] {
  const required = requiredRoles(profile, Boolean(context.personalData));
  const kept = approvals
    .filter(a => a.role !== PROPOSER[checkpoint] && required.includes(a.role))
    .map(a =>
      checkpoint === 'setup-output' && a.role === 'architect' && atLeast(profile, 'P2')
        ? concurs('architect')
        : a,
    );
  const decided = new Set(kept.filter(a => a.kind === 'decides').map(a => a.role));
  return kept.filter(
    (a, i) =>
      (a.kind === 'decides' || !decided.has(a.role)) &&
      kept.findIndex(b => b.role === a.role && b.kind === a.kind) === i,
  );
}

/**
 * What a checkpoint needs from people outside the team's roles (§3.2): each
 * is a condition with a reference to record, not a reviewer.
 */
export const checkpointConditions = (
  checkpoint: Checkpoint,
  profile: Profile,
  context: RoutingContext = {},
): string[] => {
  const acceptance = context.customerAcceptance
    ? ['Customer Acceptance Authority concurrence on the requirements (reference)']
    : [];
  switch (checkpoint) {
    case 'requirements':
      return acceptance;
    case 'setup-output':
      return [
        ...acceptance,
        ...(atLeast(profile, 'P2')
          ? ['Security architecture review (SAR) accepted by the Security Governance Office (reference)']
          : []),
        'Operability reviewed by the Service Delivery Manager (reference)',
      ];
    case 'apply-config':
      return [
        'Agent boundaries confirmed by the Agent Orchestrator / Config Owner (reference)',
        'AI Risk Assessment approved by the Security Champion and the AI Technical Governance Board before agentic pipelines are switched on (reference)',
      ];
    default:
      return [];
  }
};

export type Routing = (
  checkpoint: Checkpoint,
  profile: Profile,
  context?: RoutingContext,
) => Approval[];

export const defaultRouting: Routing = (checkpoint, profile, context = {}) =>
  fitToTeam(DEFAULT_ROUTING[checkpoint](profile, context), checkpoint, profile, context);

/** Which approvals a move between profiles adds to a checkpoint (the "what changes" text). */
export function addedApprovals(
  checkpoint: Checkpoint,
  from: Profile,
  to: Profile,
  routing: Routing = defaultRouting,
  context: RoutingContext = {},
): Approval[] {
  const before = routing(checkpoint, from, context);
  return routing(checkpoint, to, context).filter(
    approval => !before.some(b => b.role === approval.role),
  );
}
